import DarkMode from "@mui/icons-material/DarkMode";
import LightMode from "@mui/icons-material/LightMode";
import { Box, styled, Switch } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "@/app/store";
import { THEME_MODE } from "@/constants";
import { useResolvedThemeMode } from "@/features/appearance/hooks/useResolvedThemeMode";
import { setAppearancePreference } from "@/features/appearance/model/appearanceSlice";

/** Pill toggle track and thumb (px) — layout-only, not app-wide tokens.
 *  Sized to sit beside the 40px avatar in the header without out-weighing it; the reserved
 *  header width (LAYOUT_AVATAR_RESERVED_WIDTH_PX) is derived from TRACK_WIDTH. */
const TRACK_WIDTH = 52;
const TRACK_HEIGHT = 28;
const THUMB_SIZE = 22;
const TRACK_PADDING = 3;

const thumbStyles = {
  width: THUMB_SIZE,
  height: THUMB_SIZE,
  borderRadius: "50%",
  backgroundColor: "var(--theme-text-on-primary)",
  boxShadow: "0 1px 3px var(--theme-overlay-light-20)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
} as const;

const ThemeSwitch = styled(Switch)({
  width: TRACK_WIDTH,
  height: TRACK_HEIGHT,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: TRACK_PADDING,
    "&.Mui-checked": {
      transform: `translateX(${TRACK_WIDTH - THUMB_SIZE - TRACK_PADDING * 2}px)`,
      "& + .MuiSwitch-track": {
        backgroundColor: "var(--theme-surface-alt)",
        opacity: 1,
      },
    },
  },
  "& .MuiSwitch-track": {
    borderRadius: TRACK_HEIGHT / 2,
    backgroundColor: "var(--theme-overlay-olive-15)",
    border: "1px solid var(--theme-border)",
    opacity: 1,
  },
});

export function ThemeSelector() {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const resolvedMode = useResolvedThemeMode();
  const isDark = resolvedMode === THEME_MODE.DARK;

  const handleToggle = useCallback(() => {
    dispatch(setAppearancePreference(isDark ? THEME_MODE.LIGHT : THEME_MODE.DARK));
  }, [dispatch, isDark]);

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <ThemeSwitch
        checked={isDark}
        onChange={handleToggle}
        slotProps={{ input: { "aria-label": t("userMenu.themeAria") } }}
        icon={
          <Box sx={thumbStyles}>
            <LightMode sx={{ fontSize: 14, color: "var(--theme-warning)" }} />
          </Box>
        }
        checkedIcon={
          <Box sx={thumbStyles}>
            <DarkMode sx={{ fontSize: 14, color: "var(--theme-info)" }} />
          </Box>
        }
      />
    </Box>
  );
}
